import React, { useState } from 'react';
import './Contact.css';
import emailjs from '@emailjs/browser';
import Notification from './Notification';
import { IconLocation, IconMail, IconPhone, IconMessage } from './Icons';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: 'Web Application Engineering',
    budget: '₹50,000 - ₹2,00,000',
    message: '',
  });

  const [customBudget, setCustomBudget] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [notification, setNotification] = useState(null);

  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

  const isEmailJSConfigured =
    EMAILJS_SERVICE_ID !== 'YOUR_SERVICE_ID' &&
    EMAILJS_TEMPLATE_ID !== 'YOUR_TEMPLATE_ID' &&
    EMAILJS_PUBLIC_KEY !== 'YOUR_PUBLIC_KEY';

  const servicesList = [
    'Web Application Engineering',
    'Mobile App Engineering',
    'Cloud Architecture & DevOps',
    'Technical SEO & Growth',
    'Meta & Performance Ads',
    'Commercial Media Production',
    'Enterprise UI/UX Design',
    'Strategic Tech Consulting',
  ];

  const budgetOptions = [
    '₹25,000 - ₹50,000',
    '₹50,000 - ₹2,00,000',
    '₹2,00,000 - ₹5,00,000',
    'Custom Estimation / Need Quote 💡',
  ];

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9+\s-]{8,15}$/;

    if (!formData.name.trim() || formData.name.trim().length < 2) {
      newErrors.name = 'Please enter your full name (minimum 2 letters)';
    }

    if (!formData.email.trim() || !emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address (e.g. alex@company.com)';
    }

    if (formData.phone.trim() && !phoneRegex.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number (8-15 digits)';
    }

    if (!formData.message.trim() || formData.message.trim().length < 10) {
      newErrors.message = 'Please describe your project scope (at least 10 characters)';
    }

    if (formData.budget === 'Custom Estimation / Need Quote 💡' && !customBudget.trim()) {
      newErrors.customBudget = 'Please specify your target custom budget or quote requirements';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
    if (submitStatus) setSubmitStatus(null);
  };

  const handleServiceSelect = (serviceName) => {
    setFormData((prev) => ({ ...prev, service: serviceName }));
  };

  const handleBudgetSelect = (budgetVal) => {
    setFormData((prev) => ({ ...prev, budget: budgetVal }));
    if (errors.customBudget) {
      setErrors((prev) => ({ ...prev, customBudget: null }));
    }
  };

  const showNotification = (msg, type = 'info') => {
    setNotification({ message: msg, type });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showNotification('Please correct the highlighted form errors before sending.', 'warning');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    const finalBudgetValue =
      formData.budget === 'Custom Estimation / Need Quote 💡' && customBudget
        ? `Custom Estimation (${customBudget})`
        : formData.budget;

    const fullSummaryMessage = `
========================================
NEW CONSULTATION INQUIRY - TREVOX TECH
========================================

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || 'Not Provided'}
Company: ${formData.company || 'Not Provided'}
Selected Service: ${formData.service}
Estimated Budget: ${finalBudgetValue}

----------------------------------------
PROJECT SCOPE & DELIVERABLES:
----------------------------------------
${formData.message}

========================================
`;

    try {
      if (isEmailJSConfigured) {
        const result = await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            name: formData.name,
            from_name: formData.name,
            from_email: formData.email,
            email: formData.email,
            phone: formData.phone || 'N/A',
            company: formData.company || 'N/A',
            service: formData.service,
            budget: finalBudgetValue,
            message: fullSummaryMessage,
            details: fullSummaryMessage,
            to_email: 'trevoxtech@gmail.com',
            reply_to: formData.email,
          },
          EMAILJS_PUBLIC_KEY
        );

        if (result.status === 200 || result.text === 'OK') {
          setSubmitStatus('success');
          showNotification('Message Sent Successfully! 🎉 All details delivered to trevoxtech@gmail.com.', 'success');
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            service: 'Web Application Engineering',
            budget: '₹50,000 - ₹2,00,000',
            message: '',
          });
          setCustomBudget('');
          setErrors({});
          return;
        }
      }

      // Web3Forms Direct API Fallback
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: 'b94e339b-e85d-4f10-b9df-74f0c4cf19cb',
          subject: `New Inquiry from ${formData.name} (${formData.service})`,
          from_name: formData.name,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not Provided',
          company: formData.company || 'Not Provided',
          service: formData.service,
          budget: finalBudgetValue,
          message: fullSummaryMessage,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setSubmitStatus('success');
        showNotification('Message Sent Successfully! 🎉 All details delivered to trevoxtech@gmail.com.', 'success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: 'Web Application Engineering',
          budget: '₹50,000 - ₹2,00,000',
          message: '',
        });
        setCustomBudget('');
        setErrors({});
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Contact Form Error:', error);
      setSubmitStatus('error');
      showNotification('Inquiry logged. You can also email us directly at trevoxtech@gmail.com or WhatsApp +91 6369627698.', 'info');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
          duration={6000}
        />
      )}

      <div className="contact-container">
        <div className="section-header">
          <div className="section-badge">
            <span>Get In Touch</span>
          </div>
          <h2 className="section-title">Let's Discuss Your Next Big Project</h2>
          <p className="section-subtitle">
            Have an idea or a technical challenge? Send us a message and our lead engineers will reply within 2 hours.
          </p>
        </div>

        <div className="corporate-contact-layout">
          {/* Left Column: Direct Info */}
          <div className="corporate-contact-info">
            <div className="info-intro-card">
              <div className="status-live-indicator">
                <span className="live-dot-pulse" />
                <span>Engineers Available Now · 2hr Avg Reply</span>
              </div>
              <h3 className="info-main-title">Direct Leadership Communication</h3>
              <p className="info-main-desc">
                No middle managers or sales push. Speak directly with the engineers and founders who will architect your product.
              </p>
            </div>

            <div className="corporate-info-cards">
              <div className="corp-info-card">
                <div className="corp-info-icon">
                  <IconMail size={20} />
                </div>
                <div className="corp-info-details">
                  <span className="corp-info-label">Direct Email</span>
                  <a href="mailto:trevoxtech@gmail.com" className="corp-info-val link">
                    trevoxtech@gmail.com
                  </a>
                </div>
              </div>

              <div className="corp-info-card">
                <div className="corp-info-icon">
                  <IconPhone size={20} />
                </div>
                <div className="corp-info-details">
                  <span className="corp-info-label">Phone & Call</span>
                  <a href="tel:+916369627698" className="corp-info-val link">
                    +91 63696 27698
                  </a>
                </div>
              </div>

              <div className="corp-info-card">
                <div className="corp-info-icon">
                  <IconLocation size={20} />
                </div>
                <div className="corp-info-details">
                  <span className="corp-info-label">Headquarters</span>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Erode"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="corp-info-val link"
                  >
                    Erode, Tamil Nadu, India
                  </a>
                </div>
              </div>

              <div className="corp-info-card whatsapp-highlight-card">
                <div className="corp-info-icon wa-icon">
                  <IconMessage size={20} />
                </div>
                <div className="corp-info-details">
                  <span className="corp-info-label">Instant Support</span>
                  <a
                    href="https://wa.me/916369627698?text=Hello%20Trevox%20Team%2C%20I%20would%20like%20to%20inquire%20about%20your%20services."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="corp-info-val link-whatsapp"
                  >
                    Chat on WhatsApp (Instant) ➔
                  </a>
                </div>
              </div>
            </div>

            <div className="sla-guarantee-box">
              <div className="sla-badge">🛡️ 100% Confidentiality & Quality Guarantee</div>
              <p>Your ideas and project requirements are strictly protected. We sign mutual NDAs before any code is written.</p>
            </div>
          </div>

          {/* Right Column: Premium Form */}
          <div className="corporate-rfp-form-wrapper">
            <div className="rfp-form-header">
              <h3 className="rfp-form-title">Request a Free Project Estimate</h3>
              <p className="rfp-form-desc">Fill out your details below and we will prepare a clear scope and timeline.</p>
            </div>

            <form className="corporate-rfp-form" onSubmit={handleSubmit} noValidate>
              {/* Name & Email Row */}
              <div className="form-row-2">
                <div className={`corp-form-group ${errors.name ? 'has-error' : ''}`}>
                  <label htmlFor="name">Your Name *</label>
                  <div className="input-with-icon">
                    <span className="field-icon">👤</span>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g. Alexander Vance"
                    />
                  </div>
                  {errors.name && <span className="field-error-msg">⚠️ {errors.name}</span>}
                </div>

                <div className={`corp-form-group ${errors.email ? 'has-error' : ''}`}>
                  <label htmlFor="email">Email Address *</label>
                  <div className="input-with-icon">
                    <span className="field-icon">✉️</span>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="alex@company.com"
                    />
                  </div>
                  {errors.email && <span className="field-error-msg">⚠️ {errors.email}</span>}
                </div>
              </div>

              {/* Phone & Company Row */}
              <div className="form-row-2">
                <div className={`corp-form-group ${errors.phone ? 'has-error' : ''}`}>
                  <label htmlFor="phone">Phone / WhatsApp</label>
                  <div className="input-with-icon">
                    <span className="field-icon">📞</span>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  {errors.phone && <span className="field-error-msg">⚠️ {errors.phone}</span>}
                </div>

                <div className="corp-form-group">
                  <label htmlFor="company">Company / Organization</label>
                  <div className="input-with-icon">
                    <span className="field-icon">🏢</span>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="e.g. Acme Solutions"
                    />
                  </div>
                </div>
              </div>

              {/* Solution Area Chips */}
              <div className="corp-form-group">
                <label>Solution Area *</label>
                <div className="chip-selection-grid">
                  {servicesList.map((svc, i) => (
                    <button
                      type="button"
                      key={i}
                      className={`solution-chip ${formData.service === svc ? 'selected' : ''}`}
                      onClick={() => handleServiceSelect(svc)}
                    >
                      {svc}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget Chips */}
              <div className="corp-form-group">
                <label>Estimated Budget Range</label>
                <div className="budget-chips-row">
                  {budgetOptions.map((b, i) => (
                    <button
                      type="button"
                      key={i}
                      className={`budget-chip ${formData.budget === b ? 'selected' : ''}`}
                      onClick={() => handleBudgetSelect(b)}
                    >
                      {b}
                    </button>
                  ))}
                </div>

                {formData.budget === 'Custom Estimation / Need Quote 💡' && (
                  <div className={`custom-budget-input-wrapper ${errors.customBudget ? 'has-error' : ''}`} style={{ marginTop: '0.75rem' }}>
                    <div className="input-with-icon">
                      <span className="field-icon">💡</span>
                      <input
                        type="text"
                        value={customBudget}
                        onChange={(e) => {
                          setCustomBudget(e.target.value);
                          if (errors.customBudget) setErrors((prev) => ({ ...prev, customBudget: null }));
                        }}
                        placeholder="Enter your custom target budget or estimation details (e.g. ₹1,50,000 / $3,000 USD)..."
                      />
                    </div>
                    {errors.customBudget && <span className="field-error-msg">⚠️ {errors.customBudget}</span>}
                  </div>
                )}
              </div>

              {/* Scope Textarea */}
              <div className={`corp-form-group ${errors.message ? 'has-error' : ''}`}>
                <label htmlFor="message">Project Scope & Deliverables *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project goals, features needed, target deadline, or any reference links..."
                />
                {errors.message && <span className="field-error-msg">⚠️ {errors.message}</span>}
              </div>

              {submitStatus === 'success' && (
                <div className="form-alert success">
                  ✅ <strong>Message Sent Successfully!</strong> All project details have been delivered to <strong>trevoxtech@gmail.com</strong>. Our engineering directors will respond within 2 hours.
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="btn btn-primary rfp-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span>Sending Inquiry...</span>
                ) : (
                  <>
                    <span>Send Project Request</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;